import { Direction } from '@angular/cdk/bidi';
import { Component, DoCheck, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { CommonUtilsService } from '@taxilla-library/lib/services/commonutils/common-utils.service';
import { Subscription } from 'rxjs';

import { ModifiedErrorStateMatcher } from '../../extensions/errorstatematcher/errorstatematcher';

@Component({
    selector: 'material-input',
    templateUrl: './input.component.html',
    styleUrls: ['./input.component.scss'],
    standalone: false,
})
export class MaterialInputComponent implements OnInit, OnDestroy, OnChanges, DoCheck {
    @Input() placeholder;
    @Input() name;
    @Input() autofocus = false;
    @Input() title: any = '';
    @Input() model;
    @Input() id;
    @Input() classList;
    @Input() debounceAfter: number;
    @Input() options: any[];
    @Input() autoComplete: boolean;
    @Input() required: boolean;
    @Input() errors: any[];
    @Input() warnings: any[];
    @Input() disabled: boolean;
    @Input() readonly: boolean;
    @Input() type: string;
    @Input() matTooltip: string;
    @Input() hint: string;
    @Input() maxLength: number;
    @Input() needIcon: boolean;
    @Input() description: string;
    @Input() showClearText: boolean;
    @Input() appearance: MatFormFieldAppearance;
    @Input() noHints = false;
    @Input() tailwindClasses: string[];
    @Input() outputFormat: string;

    @Output() modelChange = new EventEmitter();
    @Output() debounceEventHandler = new EventEmitter();
    @Output() keyUpEventHandler = new EventEmitter();
    @Output() onDataClear = new EventEmitter();
    @Output() onBlurEvent = new EventEmitter();

    matcher = new ModifiedErrorStateMatcher();
    control = new UntypedFormControl('');
    valueSubscription: Subscription;
    dir: Direction = 'ltr';
    private lastErrorsLength: number | undefined;
    private isValueEditMode = false;

    constructor() {}

    private updateFormErrors(): void {
        if (!this.errors || (Array.isArray(this.errors) && this.errors.length === 0)) {
            if (this.control.getError('errors') !== undefined) {
                this.control.setErrors(undefined);
            }
            this.lastErrorsLength = 0;
        } else {
            this.control.setErrors({ errors: this.errors });
            this.lastErrorsLength = this.errors.length;
        }
    }

    checkArabic = () => (this.dir = /[\u0600-\u06FF]/.test(this.model?.toString() || '') ? 'rtl' : 'ltr');

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.model) {
            this.checkArabic();
            this.setFormattedValue();
        }
        if (changes.disabled) {
            if (changes.disabled.currentValue === true) {
                this.control.disable();
            } else {
                this.control.enable();
            }
        }
        if (changes.errors) {
            this.updateFormErrors();
        }
    }

    modelChanged = (_event: any) => {
        if (this.type === 'number' && this.outputFormat) {
            const normalValue = String(_event || '').replace(/,/g, '');
            this.model = normalValue;
        } else {
            this.model = typeof _event === 'string' ? _event?.trim?.() : _event;
        }

        this.checkArabic();
        this.modelChange.next(this.model);
    };

    clearData = value => {
        this.onDataClear.emit(value);
    };

    inputChange = data => {
        this.control.setValue((data.$event && data.$event.target && data.$event.target.value) || (data.target && data.target.value));
    };

    clearErrors = () => {
        if (Array.isArray(this.errors) && this.errors.length > 0) {
            this.errors.splice(0, 1);
            this.updateFormErrors();
        }
    };

    setFormattedValue = () => {
        if (this.type === 'number' && this.outputFormat && !this.model?.includes(',') && !this.isValueEditMode) {
            const formattedValue = CommonUtilsService.formatNumber(this.model, this.outputFormat);
            this.control.setValue(formattedValue);
        } else {
            this.control.setValue(typeof this.model === 'string' ? this.model?.trim?.() : this.model, { emitEvent: false });
        }
    };

    onFocus = () => {
        this.isValueEditMode = true;
        this.control.setValue(this.model);
    };

    onBlur = $event => {
        this.onBlurEvent.emit($event);
        this.isValueEditMode = false;
        this.setFormattedValue();
        this.updateFormErrors();
    };

    ngOnInit() {
        this.valueSubscription = this.control.valueChanges.subscribe(_event => _event !== this.model && this.modelChanged(_event));
        this.updateFormErrors();
    }

    ngDoCheck(): void {
        const currentLen = Array.isArray(this.errors) ? this.errors.length : 0;
        if (this.lastErrorsLength !== currentLen) {
            this.updateFormErrors();
        }
    }

    ngOnDestroy(): void {
        this.valueSubscription && this.valueSubscription.unsubscribe();
    }
}
